// React and related imports
import React, { useState } from 'react';

// Flowbite components
import { Spinner, Table, ToggleSwitch, Tooltip } from 'flowbite-react';

// Custom components
import { TableCell, TableCommon } from '../components/Table/Table';
import { Loading } from '../components/Shared/Loading/Loading';
import { PageHeader } from '../components/Shared/PageHeader';
import AddUserModal from '../components/UserModal/AddUserModal';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import { RoleSelect } from '../components/RoleSelect/RoleSelect';
import { ChangeRoleDialog } from '../components/UserModal/ChangeRoleDialog';

// i18next for translations
import { useTranslation } from 'react-i18next';

// Redux API hooks
import { useFetchUserQuery } from '../redux/api/UserListApi';
import {
  useUpdateUserRoleMutation,
  useUpdateUserStatusMutation,
} from '../redux/api/UserListApi';

// Interfaces and types
import { IUser } from '../common/interfaces/User.interface';
import { Status } from '../common/enums/Status';
import { DashboardUserRole } from '../common/enums/DashboardUserRole';

// Custom hooks
import { usePageTitle } from '../hooks/usePageTitle';

// User info context
import { useUserInfo } from '../auth/UserInfoProvider';

export const UserListPage: React.FC = () => {
  const { t } = useTranslation();
  const { userInfo } = useUserInfo();

  usePageTitle(t('pageTitles.usersManagement'));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data: response,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useFetchUserQuery({
    page: 1,
    size: 10,
  });
  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [updateUserRole] = useUpdateUserRoleMutation();
  // The role change waiting for confirmation, and the row whose role is being saved.
  const [pendingRole, setPendingRole] = useState<{
    user: IUser;
    role: DashboardUserRole;
  } | null>(null);
  const [savingRoleFor, setSavingRoleFor] = useState<string | null>(null);

  const userList: IUser[] =
    response?.data.filter((user) => user.id !== userInfo?.id) || [];

  const columnHeadLabels = [
    { id: 'name', label: t('usersManagement.name') },
    { id: 'email', label: t('usersManagement.email') },
    { id: 'role', label: t('usersManagement.role') },
    { id: 'status', label: t('usersManagement.status') },
  ];

  const handleCreateClick = () => {
    setIsModalOpen(true);
  };

  const handleStatusChange = async (userId: string, newStatus: Status) => {
    try {
      await updateUserStatus({ id: userId, status: newStatus });
    } catch (error) {
      console.error('Failed to update user status', error);
    }
  };

  const confirmRoleChange = async () => {
    if (!pendingRole) return;
    const { user, role } = pendingRole;
    setPendingRole(null);
    setSavingRoleFor(user.id);
    try {
      await updateUserRole({ id: user.id, role }).unwrap();
    } catch {
      // The toast middleware reports the failure; the select still shows the saved role.
    } finally {
      setSavingRoleFor(null);
    }
  };

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (isLoading) return <Loading />;
  return (
    <>
      <PageHeader
        title={t('sidebar.users')}
        buttonText={t('usersManagement.addUser')}
        buttonOnClick={handleCreateClick}
      />
      {userList.length > 0 ? (
        <TableCommon columns={columnHeadLabels}>
          {userList.map((item) => (
            <Table.Row key={item.id} className="bg-white">
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.email}</TableCell>
              <TableCell>
                <div className="flex items-center justify-center gap-2">
                  <RoleSelect
                    id={`role-${item.id}`}
                    sizing="sm"
                    value={item.role ?? DashboardUserRole.User}
                    disabled={savingRoleFor === item.id}
                    ariaLabel={t('usersManagement.roleFor', {
                      name: item.name,
                    })}
                    onChange={(role) => setPendingRole({ user: item, role })}
                  />
                  {savingRoleFor === item.id && <Spinner size="sm" />}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-center">
                  <Tooltip
                    content={
                      item.status === Status.ACTIVE
                        ? t('status.ACTIVE')
                        : t('status.INACTIVE')
                    }
                    placement="top"
                  >
                    <ToggleSwitch
                      checked={item.status === Status.ACTIVE}
                      onChange={() =>
                        handleStatusChange(
                          item.id,
                          item.status === Status.ACTIVE
                            ? Status.INACTIVE
                            : Status.ACTIVE
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </TableCell>
            </Table.Row>
          ))}
        </TableCommon>
      ) : (
        <EmptyState />
      )}
      <ChangeRoleDialog
        change={
          pendingRole
            ? { name: pendingRole.user.name, role: pendingRole.role }
            : null
        }
        onConfirm={confirmRoleChange}
        onCancel={() => setPendingRole(null)}
      />
      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
